// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
  useColorScheme,
} from 'react-native';
import { Avatar } from '../components/Avatar';
import { createTheme } from '../design-system/theme';
import { profileStrings } from './strings';
import type { UserProfile } from './types';

export interface MiniProfileHeaderProps {
  readonly profile: UserProfile;
}

/**
 * Header section of the mini-profile card: avatar + display name + presence
 * pill + optional emoji-status pill.
 */
export const MiniProfileHeader = React.memo<MiniProfileHeaderProps>(({ profile }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  const presenceTone = presenceColor(profile.presence, theme.colors.accent);

  // Avatar's prop is `imageUri?: string` (no `| undefined`) under
  // `exactOptionalPropertyTypes`, so we omit it entirely when the URI is
  // null instead of forwarding `undefined`.
  const avatarProps = {
    size: 'large' as const,
    initials: profile.displayName,
    presence: profile.presence === 'offline' ? null : profile.presence,
    accessibilityLabel: profile.displayName,
    ...(profile.avatarUri !== null ? { imageUri: profile.avatarUri } : {}),
  };

  return (
    <View style={styles.container}>
      <Avatar {...avatarProps} />
      <View style={styles.body}>
        <Text style={[styles.name, { color: theme.colors.text.primary }]} numberOfLines={1}>
          {profile.displayName}
        </Text>
        <Text style={[styles.username, { color: theme.colors.text.tertiary }]} numberOfLines={1}>
          @{profile.username}
        </Text>
        <View style={styles.pillRow}>
          <View style={[styles.pill, { backgroundColor: `${presenceTone}22` }]}>
            <View style={[styles.pillDot, { backgroundColor: presenceTone }]} />
            <Text style={[styles.pillText, { color: theme.colors.text.secondary }]}>
              {profileStrings.presence(profile.presence)}
            </Text>
          </View>
          {profile.statusEmoji !== null && profile.statusText !== null && (
            <View
              style={[
                styles.pill,
                {
                  backgroundColor: theme.colors.surface.tertiary,
                  marginLeft: 6,
                },
              ]}
            >
              <Text style={[styles.pillText, { color: theme.colors.text.secondary }]}>
                {profile.statusEmoji} {profile.statusText}
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
});

MiniProfileHeader.displayName = 'MiniProfileHeader';

function presenceColor(
  presence: UserProfile['presence'],
  accent: { mint: string; amber: string; slate: string },
): string {
  switch (presence) {
    case 'online':
      return accent.mint;
    case 'away':
      return accent.amber;
    case 'offline':
      return accent.slate;
  }
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    padding: 12,
  } satisfies ViewStyle,
  body: {
    marginLeft: 12,
    flex: 1,
  } satisfies ViewStyle,
  name: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 1,
  } satisfies TextStyle,
  username: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 6,
  } satisfies TextStyle,
  pillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginBottom: 4,
  } satisfies ViewStyle,
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 6,
  } satisfies ViewStyle,
  pillText: {
    fontSize: 11,
    fontWeight: '600',
  } satisfies TextStyle,
});
